import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTracker } from '../../contexts/TrackerContext'
import { formatTime12 } from '../../lib/dateUtils'
import { catClasses, catIcons } from '../../lib/constants'
import { useFamily } from '../../contexts/FamilyContext'
import { usePushSubscription } from '../../hooks/usePushSubscription'

const emptyMed = {
  id: null,
  name: '',
  purpose: '',
  dose: '',
  category: 'other',
  times: ['08:00'],
  instructions: '',
  supplyUnit: 'mL',
  doseAmount: '',
  supplyRemaining: '',
  supplyTotal: '',
  refillsRemaining: '',
  prescriptionSource: '',
  expiryDate: '',
  openedDate: '',
  daysAfterOpening: '',
  lowSupplyDays: '3',
}

export default function SettingsView() {
  const { data, toggleSetting, saveMedication, deleteMedication, exportData, openNewBottle, getMedSupplyInfo } = useTracker()
  const { family, members, activeChild, updateChild } = useFamily()
  const navigate = useNavigate()

  const [showMedModal, setShowMedModal] = useState(false)
  const [medForm, setMedForm] = useState({ ...emptyMed })
  const [showSupplySection, setShowSupplySection] = useState(false)
  const [showRefillModal, setShowRefillModal] = useState(null)
  const [editingChild, setEditingChild] = useState(false)
  const [childName, setChildName] = useState('')
  const [childDob, setChildDob] = useState('')
  const [childSex, setChildSex] = useState('')
  const [childSaving, setChildSaving] = useState(false)

  const {
    pushSupported, pushEnabled, pushPermission,
    subscribe: pushSubscribe, unsubscribe: pushUnsubscribe,
    loading: pushLoading, error: pushError, isIOS, isIOSInstalled
  } = usePushSubscription()

  const { settings, medications } = data

  function openAddMed() {
    setMedForm({ ...emptyMed })
    setShowSupplySection(false)
    setShowMedModal(true)
  }

  function openEditMed(med) {
    setMedForm({
      id: med.id,
      name: med.name,
      purpose: med.purpose || '',
      dose: med.dose || '',
      category: med.category || 'other',
      times: [...(med.times || ['08:00'])],
      instructions: med.instructions || '',
      supplyUnit: med.supplyUnit || 'mL',
      doseAmount: med.doseAmount != null ? String(med.doseAmount) : '',
      supplyRemaining: med.supplyRemaining != null ? String(med.supplyRemaining) : '',
      supplyTotal: med.supplyTotal != null ? String(med.supplyTotal) : '',
      refillsRemaining: med.refillsRemaining != null ? String(med.refillsRemaining) : '',
      prescriptionSource: med.prescriptionSource || '',
      expiryDate: med.expiryDate || '',
      openedDate: med.openedDate || '',
      daysAfterOpening: med.daysAfterOpening != null ? String(med.daysAfterOpening) : '',
      lowSupplyDays: med.lowSupplyDays != null ? String(med.lowSupplyDays) : '3',
    })
    const hasInventory = med.doseAmount != null || med.supplyTotal != null || med.expiryDate
    setShowSupplySection(!!hasInventory)
    setShowMedModal(true)
  }

  function updateMedForm(field, value) {
    setMedForm(prev => ({ ...prev, [field]: value }))
  }

  function addTimeSlot() {
    setMedForm(prev => ({ ...prev, times: [...prev.times, '08:00'] }))
  }

  function removeTimeSlot(idx) {
    setMedForm(prev => ({
      ...prev,
      times: prev.times.filter((_, i) => i !== idx)
    }))
  }

  function updateTimeSlot(idx, value) {
    setMedForm(prev => {
      const times = [...prev.times]
      times[idx] = value
      return { ...prev, times }
    })
  }

  async function handleSaveMed(e) {
    e.preventDefault()
    if (!medForm.name.trim()) return
    try {
      await saveMedication(medForm)
      setShowMedModal(false)
    } catch (err) {
      alert('Failed to save medication: ' + err.message)
    }
  }

  function handleDeleteMed(med) {
    if (window.confirm(`Delete ${med.name} from the medication schedule?`)) {
      deleteMedication(med.id)
    }
  }

  return (
    <div>
      {/* Alarm Settings */}
      <div className="t-card">
        <div className="t-card-title">Alarm Settings</div>

        <div className="t-toggle-row">
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>Medication Reminders</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
              Get notified 5 min before, at dose time, and if overdue
            </div>
          </div>
          <div
            className={`t-toggle ${settings.medAlarms ? 'on' : ''}`}
            onClick={() => toggleSetting('medAlarms')}
          >
            <div className="t-toggle-knob" />
          </div>
        </div>

        {settings.medAlarms && (
          <>
            <button
              className="t-btn t-btn-secondary t-btn-small"
              style={{ marginBottom: 12 }}
              onClick={async () => {
                if (typeof Notification === 'undefined') {
                  alert('Notifications are not supported in this browser')
                  return
                }
                const perm = await Notification.requestPermission()
                if (perm === 'granted') {
                  if ('serviceWorker' in navigator && navigator.serviceWorker.ready) {
                    const reg = await navigator.serviceWorker.ready
                    reg.showNotification('💊 Test Notification', {
                      body: 'Medication reminders are working!',
                      tag: 'test',
                      icon: '/icons/icon-192.png',
                      badge: '/icons/icon-192.png',
                    })
                  } else {
                    new Notification('💊 Test Notification', {
                      body: 'Medication reminders are working!',
                      tag: 'test',
                    })
                  }
                } else {
                  alert('Notification permission was denied. Check your browser settings.')
                }
              }}
            >
              🔔 Test Notification
            </button>

            {pushSupported && (
              <div style={{ marginBottom: 12 }}>
                <div className="t-toggle-row">
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>Background Notifications</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                      Get reminders even when the app is closed
                    </div>
                  </div>
                  <div
                    className={`t-toggle ${pushEnabled ? 'on' : ''}`}
                    style={pushLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                    onClick={() => pushEnabled ? pushUnsubscribe() : pushSubscribe()}
                  >
                    <div className="t-toggle-knob" />
                  </div>
                </div>
                {pushError && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-red)', marginTop: 4 }}>
                    {pushError}
                  </div>
                )}
                {pushPermission === 'denied' && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-red)', marginTop: 4 }}>
                    Notifications blocked. Enable them in your browser settings.
                  </div>
                )}
              </div>
            )}

            {isIOS && !isIOSInstalled && (
              <div style={{
                fontSize: '0.75rem', color: 'var(--color-text-muted)',
                marginBottom: 12, padding: '8px 12px',
                background: 'var(--color-bg)', borderRadius: 8
              }}>
                To get background notifications on iOS, add this app to your Home Screen first (Share → Add to Home Screen).
              </div>
            )}

            {pushSupported && pushEnabled && (
              <div style={{
                fontSize: '0.72rem', color: 'var(--color-text-muted)',
                marginBottom: 12, padding: '8px 12px',
                background: 'var(--color-bg)', borderRadius: 8, lineHeight: 1.5
              }}>
                <strong>Not getting notifications?</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                  <li>Check your browser allows notifications for this site</li>
                  <li>On Android: Settings → Apps → Chrome → Notifications → ensure enabled</li>
                  <li>Disable battery saver / restricted mode for your browser</li>
                  <li>Try the "Test Notification" button above to verify</li>
                </ul>
              </div>
            )}
          </>
        )}

        <div className="t-toggle-row">
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>Feed Reminders</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
              Get notified at scheduled feed times
            </div>
          </div>
          <div
            className={`t-toggle ${settings.feedAlarms ? 'on' : ''}`}
            onClick={() => toggleSetting('feedAlarms')}
          >
            <div className="t-toggle-knob" />
          </div>
        </div>

        {settings.feedAlarms && !data.feedSchedule && (
          <div style={{
            fontSize: '0.75rem', color: 'var(--color-text-muted)',
            marginBottom: 12, padding: '8px 12px',
            background: 'var(--color-bg)', borderRadius: 8
          }}>
            No feed schedule set yet. Go to the Feeding tab to create one.
          </div>
        )}

        <div className="t-toggle-row">
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>Sound Alerts</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
              Play a sound with notifications
            </div>
          </div>
          <div
            className={`t-toggle ${settings.soundAlerts ? 'on' : ''}`}
            onClick={() => toggleSetting('soundAlerts')}
          >
            <div className="t-toggle-knob" />
          </div>
        </div>
      </div>

      {/* Medication Schedule */}
      <div className="t-card">
        <div className="t-card-title">Medication Schedule</div>

        {medications.length === 0 ? (
          <div className="t-empty-state">No medications configured</div>
        ) : (
          medications.map(med => {
            const info = getMedSupplyInfo(med.id)
            return (
              <div
                className={`t-med-item ${catClasses[med.category] || 'med-other'}`}
                key={med.id}
                style={{ cursor: 'default' }}
              >
                <div className="t-med-info">
                  <div className="t-med-name">
                    {catIcons[med.category] || '💊'} {med.name}
                  </div>
                  <div className="t-med-dose">{med.dose}</div>
                  {med.purpose && <div className="t-med-detail">{med.purpose}</div>}
                  <div className="t-med-detail">
                    Times: {med.times.map(t => formatTime12(t)).join(', ')}
                  </div>
                  {med.instructions && (
                    <div className="t-med-detail">{med.instructions}</div>
                  )}
                  {info?.hasSupply && (
                    <div className="t-med-detail" style={{ marginTop: 4 }}>
                      {info.supplyRemaining != null && (
                        <span>{info.supplyRemaining}{info.supplyUnit}{info.daysRemaining != null ? ` (${Math.round(info.daysRemaining)}d)` : ''}</span>
                      )}
                      {info.refillsRemaining != null && <span> · {info.refillsRemaining} refills</span>}
                    </div>
                  )}
                  {med.prescriptionSource && (
                    <div className="t-med-detail">Rx: {med.prescriptionSource}</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0, flexDirection: 'column', alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className="t-btn t-btn-secondary t-btn-small"
                      onClick={() => openEditMed(med)}
                    >
                      Edit
                    </button>
                    <button
                      className="t-delete-btn"
                      onClick={() => handleDeleteMed(med)}
                      title="Delete medication"
                    >
                      ✕
                    </button>
                  </div>
                  {info?.supplyTotal && (
                    <button
                      className="t-btn t-btn-green t-btn-small"
                      onClick={() => setShowRefillModal(med)}
                    >
                      Refill
                    </button>
                  )}
                </div>
              </div>
            )
          })
        )}

        <button
          className="t-btn t-btn-primary"
          onClick={openAddMed}
          style={{ marginTop: 10 }}
        >
          + Add Medication
        </button>
      </div>

      {/* Data Management */}
      <div className="t-card">
        <div className="t-card-title">Data Management</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="t-btn t-btn-secondary" style={{ flex: 1 }} onClick={exportData}>
            Export All Data
          </button>
          <button className="t-btn t-btn-primary" style={{ flex: 1 }} onClick={() => navigate('/app/ai-setup')}>
            Smart Import
          </button>
        </div>
      </div>

      {/* Family Info */}
      <div className="t-card">
        <div className="t-card-title">Family Info</div>
        {family && (
          <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Family:</strong> {family.name || 'My Family'}
              <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
                Your partner needs the child's name + your PIN to join
              </div>
            </div>
            {activeChild && !editingChild && (
              <div style={{ marginBottom: 8 }}>
                <strong>Active Child:</strong> {activeChild.name}
                {activeChild.date_of_birth && (
                  <span style={{ marginLeft: 8, fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                    DOB: {activeChild.date_of_birth}
                  </span>
                )}
                {activeChild.sex && (
                  <span style={{ marginLeft: 8, fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                    Sex: {activeChild.sex === 'male' ? 'Boy' : activeChild.sex === 'female' ? 'Girl' : 'Other'}
                  </span>
                )}
                <button
                  className="t-btn t-btn-secondary t-btn-small"
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    setChildName(activeChild.name)
                    setChildDob(activeChild.date_of_birth || '')
                    setChildSex(activeChild.sex || '')
                    setEditingChild(true)
                  }}
                >
                  Edit
                </button>
              </div>
            )}
            {activeChild && editingChild && (
              <div style={{ marginBottom: 8, padding: 12, background: 'var(--color-bg)', borderRadius: 8 }}>
                <div className="t-form-row" style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 700, fontSize: '0.78rem' }}>Name</label>
                  <input
                    type="text"
                    value={childName}
                    onChange={e => setChildName(e.target.value)}
                  />
                </div>
                <div className="t-form-row" style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 700, fontSize: '0.78rem' }}>Date of Birth</label>
                  <input
                    type="date"
                    value={childDob}
                    onChange={e => setChildDob(e.target.value)}
                  />
                </div>
                <div className="t-form-row" style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 700, fontSize: '0.78rem' }}>Sex</label>
                  <select
                    value={childSex}
                    onChange={e => setChildSex(e.target.value)}
                  >
                    <option value="">Prefer not to say</option>
                    <option value="male">Boy</option>
                    <option value="female">Girl</option>
                    <option value="other">Other / intersex</option>
                  </select>
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: 8, lineHeight: 1.4 }}>
                  Used to overlay WHO weight-for-age percentile bands on the growth chart.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="t-btn t-btn-secondary t-btn-small"
                    onClick={() => setEditingChild(false)}
                    disabled={childSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="t-btn t-btn-primary t-btn-small"
                    disabled={childSaving || !childName.trim()}
                    onClick={async () => {
                      setChildSaving(true)
                      try {
                        await updateChild(activeChild.id, {
                          name: childName.trim(),
                          date_of_birth: childDob || null,
                          sex: childSex || null,
                        })
                        setEditingChild(false)
                      } catch (err) {
                        alert('Failed to update: ' + err.message)
                      } finally {
                        setChildSaving(false)
                      }
                    }}
                  >
                    {childSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            )}
            {members.length > 0 && (
              <div>
                <strong>Members:</strong>
                <ul style={{ paddingLeft: 18, marginTop: 4 }}>
                  {members.map(m => (
                    <li key={m.id} style={{ marginBottom: 2 }}>
                      {m.display_name} ({m.role || 'member'})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {!family && (
          <div className="t-empty-state">No family configured</div>
        )}
      </div>

      {/* Request Features */}
      <div className="t-card">
        <div className="t-card-title">Feedback</div>
        <a
          href="https://github.com/Trevo88423/little-legend-tracker/issues"
          target="_blank"
          rel="noopener noreferrer"
          className="t-btn t-btn-secondary"
          style={{ width: '100%', textDecoration: 'none' }}
        >
          Request a Feature or Report a Bug
        </a>
      </div>

      {/* Pay It Forward */}
      <div className="t-card">
        <div className="t-card-title">Pay It Forward</div>
        <p style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
          If Little Legend has made your days a little easier, we&apos;d love you to pay it forward.
          We don&apos;t charge for this app and never will &mdash; but if you&apos;d like to give back,
          please consider donating to the charities that supported our family:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <a
            href="https://www.cckin.com.au/donate"
            target="_blank"
            rel="noopener noreferrer"
            className="t-btn t-btn-primary"
            style={{ textDecoration: 'none' }}
          >
            Central Coast Kids in Need
          </a>
          <a
            href="https://rmhc.org.au"
            target="_blank"
            rel="noopener noreferrer"
            className="t-btn t-btn-primary"
            style={{ textDecoration: 'none', background: 'var(--color-red)' }}
          >
            Ronald McDonald House Charities
          </a>
        </div>
      </div>

      {/* Medication Form Modal */}
      {showMedModal && (
        <div className="t-modal-overlay" onClick={() => setShowMedModal(false)}>
          <div className="t-modal" onClick={e => e.stopPropagation()}>
            <div className="t-modal-handle" />
            <h3>{medForm.id ? 'Edit Medication' : 'Add Medication'}</h3>
            <form onSubmit={handleSaveMed}>
              <div className="t-form-row">
                <label>Name</label>
                <input
                  type="text"
                  placeholder="Medication name"
                  value={medForm.name}
                  onChange={e => updateMedForm('name', e.target.value)}
                  required
                />
              </div>
              <div className="t-form-row">
                <label>Dose</label>
                <input
                  type="text"
                  placeholder="e.g. 2.5mL, 1 tablet"
                  value={medForm.dose}
                  onChange={e => updateMedForm('dose', e.target.value)}
                />
              </div>
              <div className="t-form-row">
                <label>Purpose</label>
                <input
                  type="text"
                  placeholder="What is it for?"
                  value={medForm.purpose}
                  onChange={e => updateMedForm('purpose', e.target.value)}
                />
              </div>
              <div className="t-form-row">
                <label>Category</label>
                <select
                  value={medForm.category}
                  onChange={e => updateMedForm('category', e.target.value)}
                >
                  <option value="heart">Heart</option>
                  <option value="diuretic">Diuretic</option>
                  <option value="stomach">Stomach</option>
                  <option value="blood">Blood</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="t-form-row stack">
                <label>Instructions</label>
                <input
                  type="text"
                  placeholder="Special instructions..."
                  value={medForm.instructions}
                  onChange={e => updateMedForm('instructions', e.target.value)}
                />
              </div>

              <div style={{ marginTop: 12, marginBottom: 8 }}>
                <label style={{
                  fontWeight: 700, fontSize: '0.78rem',
                  color: 'var(--color-text-secondary)', display: 'block', marginBottom: 8
                }}>
                  Schedule Times
                </label>
                {medForm.times.map((t, idx) => (
                  <div className="t-form-row" key={idx}>
                    <input
                      type="time"
                      value={t}
                      onChange={e => updateTimeSlot(idx, e.target.value)}
                    />
                    {medForm.times.length > 1 && (
                      <button
                        type="button"
                        className="t-delete-btn"
                        onClick={() => removeTimeSlot(idx)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="t-btn t-btn-secondary t-btn-small"
                  onClick={addTimeSlot}
                >
                  + Add Time
                </button>
              </div>

              <div style={{ marginTop: 16, marginBottom: 8 }}>
                <button
                  type="button"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontWeight: 700, fontSize: '0.78rem', color: 'var(--color-primary)',
                    padding: 0, fontFamily: 'var(--font-family)',
                  }}
                  onClick={() => setShowSupplySection(prev => !prev)}
                >
                  {showSupplySection ? '\u25BC' : '\u25B6'} Supply & Prescription
                </button>
              </div>

              {showSupplySection && (
                <div style={{ padding: '8px 0' }}>
                  <div className="t-form-row">
                    <label>Unit</label>
                    <select value={medForm.supplyUnit} onChange={e => updateMedForm('supplyUnit', e.target.value)}>
                      <option value="mL">mL</option>
                      <option value="tablets">Tablets</option>
                      <option value="capsules">Capsules</option>
                      <option value="doses">Doses</option>
                      <option value="puffs">Puffs</option>
                    </select>
                  </div>
                  <div className="t-form-row">
                    <label>Dose Amt</label>
                    <input type="number" step="any" min="0" placeholder="e.g. 2.5"
                      value={medForm.doseAmount} onChange={e => updateMedForm('doseAmount', e.target.value)} />
                  </div>
                  <div className="t-form-row">
                    <label>Current</label>
                    <input type="number" step="any" min="0" placeholder="Remaining supply"
                      value={medForm.supplyRemaining} onChange={e => updateMedForm('supplyRemaining', e.target.value)} />
                  </div>
                  <div className="t-form-row">
                    <label>Full Size</label>
                    <input type="number" step="any" min="0" placeholder="Full bottle/pack"
                      value={medForm.supplyTotal} onChange={e => updateMedForm('supplyTotal', e.target.value)} />
                  </div>
                  <div className="t-form-row">
                    <label>Refills</label>
                    <input type="number" min="0" placeholder="Remaining refills"
                      value={medForm.refillsRemaining} onChange={e => updateMedForm('refillsRemaining', e.target.value)} />
                  </div>
                  <div className="t-form-row">
                    <label>Prescriber</label>
                    <input type="text" placeholder="e.g. Dr Smith (GP)"
                      value={medForm.prescriptionSource} onChange={e => updateMedForm('prescriptionSource', e.target.value)} />
                  </div>
                  <div className="t-form-row">
                    <label>Expiry</label>
                    <input type="date" value={medForm.expiryDate}
                      onChange={e => updateMedForm('expiryDate', e.target.value)} />
                  </div>
                  <div className="t-form-row">
                    <label>Opened</label>
                    <input type="date" value={medForm.openedDate}
                      onChange={e => updateMedForm('openedDate', e.target.value)} />
                  </div>
                  <div className="t-form-row">
                    <label>Days Valid</label>
                    <input type="number" min="0" placeholder="Days after opening"
                      value={medForm.daysAfterOpening} onChange={e => updateMedForm('daysAfterOpening', e.target.value)} />
                  </div>
                  <div className="t-form-row">
                    <label>Low Alert</label>
                    <input type="number" min="0" placeholder="Days of supply"
                      value={medForm.lowSupplyDays} onChange={e => updateMedForm('lowSupplyDays', e.target.value)} />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button
                  type="button"
                  className="t-btn t-btn-secondary"
                  style={{ flex: 1 }}
                  onClick={() => setShowMedModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="t-btn t-btn-primary" style={{ flex: 1 }}>
                  {medForm.id ? 'Save Changes' : 'Add Medication'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRefillModal && (
        <div className="t-modal-overlay" onClick={() => setShowRefillModal(null)}>
          <div className="t-modal" onClick={e => e.stopPropagation()}>
            <div className="t-modal-handle" />
            <h3>Refill {showRefillModal.name}</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: 16 }}>
              This will reset supply to {showRefillModal.supplyTotal}{showRefillModal.supplyUnit || 'mL'},
              set opened date to today{showRefillModal.refillsRemaining != null ? `, and use 1 of ${showRefillModal.refillsRemaining} remaining refills` : ''}.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="t-btn t-btn-secondary" style={{ flex: 1 }} onClick={() => setShowRefillModal(null)}>
                Cancel
              </button>
              <button className="t-btn t-btn-green" style={{ flex: 1 }} onClick={() => {
                openNewBottle(showRefillModal.id)
                setShowRefillModal(null)
              }}>
                Open New Bottle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
